import React from 'react';

const PitPayTermsContent = () => (
  <>
    <h1>PIT PAY TERMS OF SERVICE</h1>
    <p className="last-updated">Last Updated: January 12, 2026</p>

    <p>
      These Terms of Service ("<strong>Terms</strong>" or "
      <strong>Agreement</strong>") govern Promoter's use of the Pit Pay platform
      and services. By using the Platform, Promoter agrees to be bound by these
      Terms.
    </p>

    <h3>1. DEFINITIONS</h3>
    <p>
      "<strong>Event</strong>" means any racing or motorsports event operated by
      Promoter for which Passes are sold through the Platform.
    </p>
    <p>
      "<strong>Participant</strong>" means any individual who purchases a Pass,
      membership, or item through the Platform.
    </p>
    <p>
      "<strong>Pass</strong>" means any pit pass, credential, membership, or
      other item sold by Promoter through the Platform.
    </p>
    <p>
      "<strong>Platform</strong>" means the Pit Pay mobile application, website,
      and related software and services.
    </p>
    <p>
      "<strong>Promoter Content</strong>" means all content, information,
      images, and materials provided by Promoter for use on the Platform.
    </p>
    <p>
      "<strong>Release Agreement</strong>" means any waiver, release of
      liability, or similar agreement required to be signed by Participants.
    </p>
    <p>
      "<strong>Representatives</strong>" means Promoter's employees, agents,
      contractors, and other authorized personnel.
    </p>
    <p>
      "<strong>Transaction Data</strong>" means all data generated through
      Promoter's use of the Platform, including Participant information,
      purchase records, and Event attendance data.
    </p>

    <h3>2. SOFTWARE LICENSE AND PERMITTED USE</h3>
    <h4>2.1 License Grant</h4>
    <p>
      Pit Pay grants Promoter a limited, non-exclusive, non-transferable license
      to download and use the Platform solely for internal business purposes
      related to Events.
    </p>
    <h4>2.2 Proprietary Rights</h4>
    <p>
      Pit Pay owns all right, title, and interest in and to the Platform,
      including any enhancements or modifications developed based on ideas or
      feedback communicated by Promoter.
    </p>
    <h4>2.3 Restrictions</h4>
    <p>
      Promoter shall not (and shall ensure Representatives do not) copy, modify,
      reverse engineer, decompile, or attempt to gain access to the source code
      of the Platform.
    </p>

    <h3>3. PROMOTER RESPONSIBILITIES</h3>
    <h4>3.1 Legal Compliance</h4>
    <p>
      Promoter must comply with all applicable federal, state, and local laws,
      rules, and regulations and secure all necessary licenses and permissions
      for Promoter Content and the operation of Events.
    </p>
    <h4>3.2 Waiver and Release Agreements</h4>
    <p className="indent">
      <strong>(a) Waiver Responsibility Allocation.</strong> Promoter is solely
      responsible for the sufficiency and enforceability of all Release
      Agreement language, obtaining insurance company approval for such
      language, and ensuring the Release Agreement is appropriate for Promoter's
      jurisdiction and event type. Pit Pay is responsible for providing a
      digital signature collection method that complies with the Electronic
      Signatures in Global and National Commerce Act (E-SIGN Act) and applicable
      state electronic signature laws, and for maintaining retrievable copies of
      executed Release Agreements in accordance with these Terms.
    </p>
    <p className="indent">
      <strong>(b) Minor Participant Waivers.</strong> Promoter acknowledges that
      the enforceability of Release Agreements signed by parents or legal
      guardians on behalf of minor participants varies by jurisdiction. Promoter
      is solely responsible for consulting with their insurance provider and
      legal counsel regarding the enforceability of minor waivers in Promoter's
      operating jurisdiction.
    </p>
    <p className="indent">
      <strong>(c) Waiver Verification.</strong> Promoter is responsible for
      verifying that the Release Agreement served through the Platform is
      current and approved by Promoter's insurance provider prior to each Event.
    </p>
    <p className="indent">
      <strong>(d) On-Site Waiver Process.</strong> In the event a Participant's
      Release Agreement cannot be verified through the Platform at check-in,
      Promoter is responsible for obtaining a signed paper waiver before
      granting access.
    </p>
    <h4>3.3 On-Site Operations</h4>
    <p className="indent">
      <strong>(a) Equipment.</strong> Promoter is responsible for procuring,
      maintaining, and operating all equipment necessary for Event check-in and
      operations, including mobile devices, tablets, scanners, computers, and
      internet connectivity. Pit Pay does not provide hardware.
    </p>
    <p className="indent">
      <strong>(b) Check-In Process.</strong> Promoter is responsible for
      establishing and executing the check-in process at Events, including
      verifying Participant credentials, issuing armbands or other physical
      credentials, and ensuring only authorized Participants gain access.
    </p>
    <p className="indent">
      <strong>(c) Designated Location.</strong> Promoter must provide an
      expedited experience for Pit Pay users, including a designated window or
      pick-up location with appropriate signage.
    </p>
    <h4>3.4 Training</h4>
    <p>
      Promoter is responsible for ensuring all Representatives understand how
      the Platform works end-to-end and how to properly execute on-site
      processes. Pit Pay provides training resources, but Promoter is solely
      responsible for ensuring staff competency.
    </p>
    <h4>3.5 Capacity and Reserved Inventory</h4>
    <p className="indent">
      <strong>(a) Accurate Information.</strong> Promoter is solely responsible
      for providing accurate capacity limits, maps, seating charts, and
      inventory information for all Events.
    </p>
    <p className="indent">
      <strong>(b) Map and Configuration Verification.</strong> Promoter must
      review and verify all maps, seating charts, and configurations BEFORE
      sales begin. Pit Pay is not responsible for errors resulting from
      inaccurate or incomplete information.
    </p>
    <p className="indent">
      <strong>(c) Double Booking.</strong> Pit Pay shall have no liability for
      any double booking of reserved items resulting from inaccurate inventory
      information provided by Promoter.
    </p>
    <p className="indent">
      <strong>(d) Overselling.</strong> Promoter is solely responsible for any
      overselling of capacity resulting from inaccurate information. Pit Pay
      shall have no liability for any claims arising from Participants denied
      entry due to capacity issues.
    </p>
    <h4>3.6 Non-Transferable Passes</h4>
    <p>
      All Passes, memberships, and credentials sold through the Platform are
      non-transferable and valid only for the individual Participant who
      purchased or registered for them. Promoter shall enforce the
      non-transferable nature of Passes at check-in.
    </p>
    <h4>3.7 Account Access and User Management</h4>
    <p className="indent">
      <strong>(a) User Accounts.</strong> Promoter may create multiple user
      accounts with varying permission levels. Promoter is solely responsible
      for managing user accounts.
    </p>
    <p className="indent">
      <strong>(b) Credential Security.</strong> Promoter is responsible for
      maintaining the security of all login credentials and ensuring only
      authorized individuals have access.
    </p>
    <p className="indent">
      <strong>(c) Responsibility for Users.</strong> Promoter is responsible for
      all actions taken by users with access to Promoter's account, whether
      authorized or unauthorized.
    </p>

    <h3>4. INSURANCE</h3>
    <h4>4.1 Coverage Required</h4>
    <p>
      Promoter must maintain commercial general liability insurance covering
      bodily injury and property damage arising from Events. Promoter shall name
      Pit Pay, Inc. and its affiliates as additional insureds on such policy.
    </p>
    <h4>4.2 Insurance Notification</h4>
    <p>
      Promoter shall notify Pit Pay of the insurance company and policy
      providing coverage for Events. Promoter shall notify Pit Pay immediately
      of any change in insurance carrier, policy, or coverage, particularly if
      Promoter has any scheduled or active Events in the Platform. This
      notification enables Pit Pay to serve the appropriate Release Agreement
      and waiver documents for Promoter's Events.
    </p>
    <h4>4.3 Insurance Approval</h4>
    <p>
      Promoter must obtain approval from their insurance company and sanctioning
      body regarding the enforceability of Release Agreements and the use of the
      Platform to collect digital signatures.
    </p>
    <h4>4.4 Proof of Insurance</h4>
    <p>
      Promoter shall provide a certificate of insurance upon request by Pit Pay.
    </p>
    <h4>4.5 Recommended Additional Coverage</h4>
    <p>
      Pit Pay strongly recommends Promoter maintain the following additional
      coverage:
    </p>
    <p className="indent-more">(i) Participant accident insurance</p>
    <p className="indent-more">
      (ii) Property insurance covering facility, equipment, and assets
    </p>
    <p className="indent-more">
      (iii) Technology errors and omissions (Tech E&amp;O) insurance
    </p>
    <p className="indent-more">
      (iv) Cyber liability insurance covering data breaches and privacy claims
    </p>
    <p>
      Note: Promoter's employees handle Participant data; Promoter is
      responsible for data security at its facilities.
    </p>

    <h3>5. PAYMENT PROCESSING AND FEES</h3>
    <h4>5.1 Payment Processing Structure</h4>
    <p className="indent">
      <strong>(a) Third-Party Processor.</strong> Pit Pay utilizes Stripe, Inc.
      and its Stripe Connect platform ("<strong>Payment Processor</strong>") to
      facilitate payment processing and fund distribution.
    </p>
    <p className="indent">
      <strong>(b) Marketplace Model.</strong> The Platform operates as a
      marketplace whereby Participants purchase Passes from Promoter, with
      payments processed through Pit Pay's Payment Processor infrastructure.
    </p>
    <p className="indent">
      <strong>(c) Merchant of Record.</strong> Promoter acknowledges that Pit
      Pay serves as the merchant of record for transactions processed through
      the Platform.
    </p>
    <p className="indent">
      <strong>(d) Payment Processor Terms.</strong> Promoter agrees to be bound
      by Payment Processor terms as a condition of using the Platform.
    </p>
    <h4>5.2 Fees and Payouts</h4>
    <p className="indent">
      <strong>(a) Transaction Fees.</strong> Pit Pay charges Transaction Fees as
      outlined in Exhibit A (Fee Schedule). These fees are non-refundable.
    </p>
    <p className="indent">
      <strong>(b) Remittance.</strong> Pit Pay will remit 100% of the price set
      by Promoter, less applicable Transaction Fees.
    </p>
    <p className="indent">
      <strong>(c) Payout Timing.</strong> Pit Pay holds funds until after the
      Event date. ACH payouts typically occur approximately four (4) business
      days after the Event.
    </p>
    <p className="indent">
      <strong>(d) Payout Withholding.</strong> Pit Pay may withhold or delay
      payouts until all pending refund, credit, or Buyer Protection requests for
      an Event are resolved and settled.
    </p>
    <h4>5.3 Payment Method on File</h4>
    <p className="indent">
      <strong>(a) Requirement.</strong> Promoter must maintain a valid payment
      method on file at all times.
    </p>
    <p className="indent">
      <strong>(b) Authorization to Charge.</strong> Promoter authorizes Pit Pay
      to charge such payment method for chargebacks, account shortfalls, fees,
      and any other amounts owed.
    </p>
    <p className="indent">
      <strong>(c) Failed Charges.</strong> If a charge fails, Promoter shall
      provide an updated payment method within five (5) business days.
    </p>
    <h4>5.4 Chargebacks and Disputes</h4>
    <p className="indent">
      <strong>(a) Promoter Responsibility.</strong> Promoter is solely
      responsible for all chargebacks, disputes, and fraud claims, including the
      disputed amount and any fees (currently $15 per dispute).
    </p>
    <p className="indent">
      <strong>(b) Cooperation.</strong> Promoter shall cooperate with Pit Pay in
      responding to chargebacks.
    </p>
    <h4>5.5 Taxes</h4>
    <p>
      Promoter is responsible for collecting and paying all applicable taxes
      arising from the sale of Passes (other than taxes on Pit Pay's income).
    </p>
    <h4>5.6 Product-Specific Compliance</h4>
    <p className="indent">
      <strong>(a) Product-Specific Taxes and Fees.</strong> Promoter is solely
      responsible for all product-specific taxes and fees including tire
      disposal fees, environmental fees, fuel taxes, motor fuel surcharges,
      hazardous materials fees, and recycling fees.
    </p>
    <p className="indent">
      <strong>(b) Regulatory Compliance.</strong> Promoter is solely responsible
      for ensuring all products sold comply with applicable laws and regulations
      including licensing, safety standards, environmental regulations, and
      consumer protection laws. Pit Pay makes no representations regarding the
      legality or compliance of products sold through the Platform.
    </p>
    <h4>5.7 1% Cash Back Program</h4>
    <p className="indent">
      <strong>(a) Availability.</strong> Where Participant-Paid fee structures
      are in effect, a 1% Cash Back contribution shall be calculated on
      processing volume.
    </p>
    <p className="indent">
      <strong>(b) Annual Payout.</strong> Pit Pay shall pay the accumulated
      balance annually following the calendar year end.
    </p>
    <p className="indent">
      <strong>(c) Right to Withhold.</strong> Pit Pay may offset amounts owed
      against the Cash Back balance.
    </p>
    <p className="indent">
      <strong>(d) Inapplicability.</strong> Does not apply where all-in pricing
      is required or elected.
    </p>

    <h3>6. POINT OF SALE SYSTEM</h3>
    <p className="indent">
      <strong>(a) Availability.</strong> Pit Pay may provide a Point of Sale
      (POS) system for walk-up and on-site sales.
    </p>
    <p className="indent">
      <strong>(b) Equipment.</strong> Promoter is responsible for purchasing and
      maintaining all POS hardware including tablets, receipt printers, and card
      readers. Pit Pay does not provide hardware.
    </p>
    <p className="indent">
      <strong>(c) All-In Pricing.</strong> Walk-up POS sales use all-in pricing
      only. Fees are deducted from Promoter's payout.
    </p>
    <p className="indent">
      <strong>(d) Training.</strong> Pit Pay provides training resources.
      Promoter is responsible for ensuring staff are properly trained on POS
      operations.
    </p>
    <p className="indent">
      <strong>(e) Internet Connectivity.</strong> Promoter is responsible for
      providing reliable internet connectivity at POS locations.
    </p>

    <h3>7. REFUNDS, CANCELLATIONS, AND CREDITS</h3>
    <h4>7.1 Refund Policy</h4>
    <p className="indent">
      <strong>(a) Default Policy.</strong> Unless otherwise specified, all sales
      are non-refundable. Promoter may establish a more favorable refund policy.
    </p>
    <p className="indent">
      <strong>(b) Refund Policy Disclosure.</strong> Promoter must clearly
      display its refund policy on Event listings.
    </p>
    <p className="indent">
      <strong>(c) Transaction Fees on Refunds.</strong> All fees associated with
      the original transaction are non-refundable to Promoter.
    </p>
    <h4>7.2 Event Cancellation</h4>
    <p className="indent">
      <strong>(a) Full Cancellation.</strong> If an Event is cancelled, Promoter
      shall issue refunds or credits to all Participants.
    </p>
    <p className="indent">
      <strong>(b) Postponement.</strong> Promoter may transfer purchases to
      rescheduled Events but should offer refunds to those who cannot attend.
    </p>
    <p className="indent">
      <strong>(c) Partial Completion.</strong> Refund decisions for partially
      completed Events are at Promoter's discretion, subject to applicable law.
    </p>
    <h4>7.3 Credits</h4>
    <p className="indent">
      <strong>(a) Credit Issuance.</strong> Promoter may issue Platform credits
      in lieu of refunds, subject to applicable law.
    </p>
    <p className="indent">
      <strong>(b) Credit Compliance.</strong> Promoter is responsible for
      complying with all laws governing credits and stored value.
    </p>
    <p className="indent">
      <strong>(c) Credit Obligations Survive Termination.</strong> Promoter
      remains responsible for honoring credits after termination.
    </p>
    <h4>7.4 Buyer Protection Program</h4>
    <p className="indent">
      <strong>(a) Program Options.</strong> Promoter may opt to offer
      Participants one or both of the following Buyer Protection options at
      checkout: (i) Credit for Any Reason - Participant receives Platform credit
      if unable to attend; (ii) Refund for Any Reason - Participant receives a
      refund if unable to attend.
    </p>
    <p className="indent">
      <strong>(b) Opt-In.</strong> Promoter's election to offer Buyer Protection
      programs is made through Platform settings.
    </p>
    <p className="indent">
      <strong>(c) Binding Commitment.</strong> If Promoter opts into a Buyer
      Protection program, Promoter must honor all requests from Participants.
      Requests are processed automatically without requiring justification.
    </p>
    <p className="indent">
      <strong>(d) No Questions Asked.</strong> Buyer Protection programs are
      marketed as providing Participants peace of mind and flexibility. Promoter
      may not contest, deny, or circumvent legitimate Buyer Protection requests.
    </p>
    <p className="indent">
      <strong>(e) Financial Responsibility.</strong> The credit or refund amount
      (less any non-refundable fees) will be deducted from Promoter's payout or
      charged to Promoter's payment method on file.
    </p>
    <p className="indent">
      <strong>(f) Marketing.</strong> Pit Pay may market Buyer Protection
      programs at checkout and in promotional materials when Promoter has opted
      in.
    </p>
    <p className="indent">
      <strong>(g) Program Terms.</strong> Pit Pay may establish terms,
      conditions, and deadlines for Buyer Protection requests. Pit Pay may
      modify program terms upon notice.
    </p>
    <p className="indent">
      <strong>(h) Timely Resolution.</strong> Promoter must resolve Buyer
      Protection requests in a timely manner. Failure may result in payout
      withholding or Pit Pay processing requests on Promoter's behalf.
    </p>

    <h3>8. REGULATORY COMPLIANCE</h3>
    <h4>8.1 Fee Display and Pricing</h4>
    <p className="indent">
      <strong>(a) Compliance Obligation.</strong> Promoter is solely responsible
      for complying with all fee display and pricing laws in its jurisdiction.
    </p>
    <p className="indent">
      <strong>(b) Platform Options.</strong> The Platform provides all-in
      pricing and itemized pricing options. Promoter must select the method that
      complies with applicable law.
    </p>
    <p className="indent">
      <strong>(c) Mandatory All-In Pricing.</strong> Pit Pay may require all-in
      pricing based on jurisdiction or regulatory requirements.
    </p>
    <h4>8.2 General Compliance</h4>
    <p>
      Promoter shall comply with all applicable laws governing live event
      ticketing, consumer protection, refund disclosures, and gift card/stored
      value regulations.
    </p>

    <h3>9. DATA AND INTELLECTUAL PROPERTY</h3>
    <h4>9.1 Data Ownership</h4>
    <p>
      Promoter retains ownership of all Promoter Content and Transaction Data.
    </p>
    <h4>9.2 License to Pit Pay</h4>
    <p>
      Promoter grants Pit Pay a non-exclusive, royalty-free license to use
      Promoter Content and Transaction Data as necessary to provide the
      Platform.
    </p>
    <h4>9.3 Aggregated Data</h4>
    <p>
      Pit Pay may aggregate and anonymize data for product improvement,
      benchmarking, and industry reporting.
    </p>
    <h4>9.4 Privacy Compliance</h4>
    <p>
      Each party shall comply with applicable data protection and privacy laws,
      including CCPA.
    </p>
    <h4>9.5 Data Security</h4>
    <p>
      Pit Pay shall maintain commercially reasonable safeguards to protect
      Transaction Data.
    </p>
    <h4>9.6 Data Breach Notification</h4>
    <p>
      Pit Pay shall notify Promoter within seventy-two (72) hours of discovering
      a security breach affecting Transaction Data.
    </p>
    <h4>9.7 Release Agreement Retention</h4>
    <p className="indent">
      <strong>(a)</strong> Pit Pay retains electronic copies of all executed
      e-sign Release Agreements for a minimum of sixty (60) months (five years)
      following the Event date and makes them available for download through the
      Platform.
    </p>
    <p className="indent">
      <strong>(b)</strong> After sixty (60) months, Pit Pay may archive or
      delete Release Agreements at its sole discretion.
    </p>
    <p className="indent">
      <strong>(c)</strong> Promoter is solely responsible for downloading and
      retaining copies of Release Agreements necessary to satisfy its own
      records retention requirements, including retention periods applicable to
      minor participants.
    </p>
    <p className="indent">
      <strong>(d)</strong> In the event of any incident involving personal
      injury, property damage, or death, Promoter should immediately download
      all relevant Release Agreements.
    </p>
    <p className="indent">
      <strong>(e)</strong> Upon termination, Pit Pay will provide Promoter no
      less than ninety (90) days to download all Release Agreements.
    </p>
    <h4>9.8 Marks</h4>
    <p>
      Promoter grants Pit Pay a royalty-free license to use Promoter's marks for
      marketing. Pit Pay grants Promoter a license to use Pit Pay marks for
      Platform-related marketing.
    </p>

    <h3>10. MESSAGING AND COMMUNICATIONS</h3>
    <h4>10.1 Messaging Services</h4>
    <p>
      The Platform provides SMS, email, and push notification capabilities ("
      <strong>Messaging Services</strong>").
    </p>
    <h4>10.2 Messaging Allocation</h4>
    <p>
      Promoter receives up to 10,000 SMS messages per calendar year at no
      charge. Additional messages are charged at established rates. Push
      notifications are unlimited.
    </p>
    <h4>10.3 Compliance</h4>
    <p>
      Promoter is solely responsible for complying with TCPA, CAN-SPAM, and all
      other applicable messaging laws.
    </p>
    <h4>10.4 Messaging Indemnification</h4>
    <p>
      Promoter shall indemnify Pit Pay from all claims arising from Promoter's
      use of Messaging Services.
    </p>

    <h3>11. TERM AND TERMINATION</h3>
    <h4>11.1 Term</h4>
    <p>
      These Terms commence upon Promoter's acceptance and continue until
      terminated.
    </p>
    <h4>11.2 Termination for Convenience</h4>
    <p>Either party may terminate upon ninety (90) days' written notice.</p>
    <h4>11.3 Termination for Cause</h4>
    <p>
      Either party may terminate immediately if the other materially breaches
      and fails to cure within fifteen (15) days.
    </p>
    <h4>11.4 Effect of Termination</h4>
    <p className="indent">
      <strong>(a) Pending Events.</strong> Pit Pay will process transactions for
      Events within thirty (30) days of termination.
    </p>
    <p className="indent">
      <strong>(b) Outstanding Amounts.</strong> Termination does not relieve
      payment obligations.
    </p>
    <p className="indent">
      <strong>(c) Release Agreements.</strong> Promoter has ninety (90) days to
      download Release Agreements after termination.
    </p>
    <p className="indent">
      <strong>(d) Credits.</strong> Promoter remains responsible for honoring
      outstanding credits.
    </p>

    <h3>12. LIABILITY AND INDEMNIFICATION</h3>
    <h4>12.1 Promoter Indemnification</h4>
    <p>
      Promoter shall indemnify Pit Pay from all claims arising from: personal
      injury, property damage, or death at Events; Promoter's acts or omissions;
      breach of these Terms; and violation of applicable law.
    </p>
    <h4>12.2 Limitation of Liability</h4>
    <p>
      <strong>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, PIT PAY'S AGGREGATE LIABILITY
        SHALL NOT EXCEED THE TOTAL FEES RECEIVED FROM PROMOTER IN THE NINETY
        (90) DAYS PRECEDING THE CLAIM. PIT PAY SHALL NOT BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
      </strong>
    </p>
    <h4>12.3 Disclaimer of Warranties</h4>
    <p>
      <strong>
        THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS
        OR IMPLIED.
      </strong>
    </p>

    <h3>13. GENERAL PROVISIONS</h3>
    <h4>13.1 Governing Law</h4>
    <p>
      These Terms are governed by North Carolina law. Disputes shall be resolved
      in Mecklenburg County, North Carolina.
    </p>
    <h4>13.2 Assignment</h4>
    <p>
      Promoter may not assign these Terms without Pit Pay's consent. Pit Pay may
      assign in connection with a merger or acquisition.
    </p>
    <h4>13.3 Entire Agreement</h4>
    <p>
      These Terms, together with all exhibits, constitute the entire agreement.
      Pit Pay may update the Fee Schedule upon thirty (30) days' notice.
    </p>
    <h4>13.4 Severability</h4>
    <p>
      If any provision is unenforceable, it shall be modified or severed without
      affecting other provisions.
    </p>
    <h4>13.5 Electronic Acceptance</h4>
    <p>
      By using the Platform, Promoter agrees to these Terms. Electronic
      acceptance is legally binding.
    </p>

    <h3 className="exhibit-title">EXHIBIT A: FEE SCHEDULE</h3>
    <p>
      The following fees apply to sales processed through the Platform. Fees are
      subject to change upon thirty (30) days' written notice.
    </p>

    <h4>1. Fee Components</h4>
    <table>
      <thead>
        <tr>
          <th>Component</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Platform Fee</td>
          <td data-label="Description">
            Pit Pay's fee for Platform access and services
          </td>
        </tr>
        <tr>
          <td>Processing Fee</td>
          <td data-label="Description">
            3.25% (all-in pricing) or 4.25% (participant-paid, includes 1% Cash
            Back)
          </td>
        </tr>
        <tr>
          <td>Transaction Fee</td>
          <td data-label="Description">
            $0.30 per online transaction; $0.10 per POS transaction
          </td>
        </tr>
      </tbody>
    </table>

    <h4>2. Online Sales - All-In Pricing</h4>
    <p>
      Fees are included in the displayed price and deducted from Promoter's
      payout.
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Pit Passes</td>
          <td data-label="Platform Fee">$1.85</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Memberships</td>
          <td data-label="Platform Fee">$4.75</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Registrations</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>General Items (&le;$500)</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>General Items (&gt;$500)</td>
          <td data-label="Platform Fee">$10.00 max</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Reserved Items</td>
          <td data-label="Platform Fee">Custom</td>
          <td data-label="Processing">Custom</td>
          <td data-label="Transaction">Custom</td>
        </tr>
      </tbody>
    </table>

    <h4>3. Online Sales - Participant-Paid Pricing</h4>
    <p>
      Fees are charged to the Participant in addition to the item price.
      Processing fee includes 1% Cash Back.
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Pit Passes</td>
          <td data-label="Platform Fee">$1.85</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Memberships</td>
          <td data-label="Platform Fee">$4.75</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Registrations</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>General Items (&le;$500)</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>General Items (&gt;$500)</td>
          <td data-label="Platform Fee">$10.00 max</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Reserved Items</td>
          <td data-label="Platform Fee">Custom</td>
          <td data-label="Processing">Custom</td>
          <td data-label="Transaction">Custom</td>
        </tr>
      </tbody>
    </table>

    <h4>4. Walk-Up POS Sales</h4>
    <p>
      All POS sales use all-in pricing. Fees are deducted from Promoter's
      payout.
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>All Items</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.10</td>
        </tr>
      </tbody>
    </table>

    <h4>5. Understanding the $500 Platform Fee Cap</h4>
    <p>
      For General Items over $500, the 2% Platform Fee is calculated only on the
      first $500, resulting in a maximum Platform Fee of $10.00. Processing and
      Transaction Fees apply to the full amount.
    </p>
    <p>
      <strong>
        <em>Example 1: $800 Item (All-In Pricing)</em>
      </strong>
    </p>
    <ul>
      <li>Platform Fee: 2% of $500 = $10.00 (capped)</li>
      <li>Processing Fee: 3.25% of $800 = $26.00</li>
      <li>Transaction Fee: $0.30</li>
      <li>Total Fees: $36.30</li>
    </ul>
    <p>
      Note: Without the cap, Platform Fee would be $16.00. The cap saves $6.00.
    </p>
    <p>
      <strong>
        <em>Example 2: $300 Item (All-In Pricing)</em>
      </strong>
    </p>
    <ul>
      <li>Platform Fee: 2% of $300 = $6.00</li>
      <li>Processing Fee: 3.25% of $300 = $9.75</li>
      <li>Transaction Fee: $0.30</li>
      <li>Total Fees: $16.05</li>
    </ul>

    <h4>6. Additional Fees</h4>
    <table>
      <thead>
        <tr>
          <th>Fee Type</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>International Cards</td>
          <td data-label="Amount">+1.5% additional processing fee</td>
        </tr>
        <tr>
          <td>Chargeback Fee</td>
          <td data-label="Amount">
            $15 per dispute (subject to change by Payment Processor)
          </td>
        </tr>
        <tr>
          <td>Reserved Items</td>
          <td data-label="Amount">
            Custom pricing (pit stalls, camping, reserved seating, map-based
            inventory)
          </td>
        </tr>
        <tr>
          <td>Forms/Waivers</td>
          <td data-label="Amount">
            Custom forms or waiver requirements may affect pricing
          </td>
        </tr>
      </tbody>
    </table>

    <h4>7. Important Notes</h4>
    <ul>
      <li>All fees are non-refundable regardless of who pays them.</li>
      <li>
        The 1% Cash Back (included in Participant-Paid processing fee) is paid
        to Promoter annually.
      </li>
      <li>
        Reserved Items include pit stalls, camping spots, reserved seating, and
        any map-based inventory.
      </li>
      <li>
        Pit Pay may modify this Fee Schedule upon thirty (30) days' written
        notice.
      </li>
    </ul>
  </>
);

export default PitPayTermsContent;
